import React, { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Bug, CheckCircle2, FileText, HelpCircle, Loader2, LogOut, Mic, Play, RefreshCw, Save, SlidersHorizontal, Trash2, Volume2, XCircle } from 'lucide-react';
import { VoiceChatModal, type VoiceDebugSettings } from '../components/VoiceChatModal';
import {
  useAdminApi,
  type DebugAudioMeta,
  type DebugConversationDetail,
  type DebugConversationMessage,
  type DebugConversationSummary,
  type DebugEvaluation,
  type DebugProfile,
  type VocabWord,
} from '../hooks/useAdminApi';

interface DebugPageProps {
  isDarkMode: boolean;
}

interface DebugAudioControlProps {
  chatId: string;
  audio: DebugAudioMeta;
  getDebugAudioUrl: (chatId: string, audioId: string) => Promise<string | null>;
}

const formatDateTime = (value?: string | null): string => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDuration = (ms?: number): string => {
  if (!ms || ms <= 0) return '-';
  return `${(ms / 1000).toFixed(1)}s`;
};

const DebugAudioControl: React.FC<DebugAudioControlProps> = ({ chatId, audio, getDebugAudioUrl }) => {
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const handleLoad = async () => {
    if (audioUrl || loading) return;
    setLoading(true);
    setError(null);
    const nextUrl = await getDebugAudioUrl(chatId, audio.audio_id);
    if (nextUrl) {
      setAudioUrl(nextUrl);
    } else {
      setError('Audio konnte nicht geladen werden.');
    }
    setLoading(false);
  };

  return (
    <div className="mt-2 flex min-w-0 flex-col gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <span className="truncate font-mono">{audio.audio_id}</span>
        <span className="shrink-0">{formatDuration(audio.duration_ms)}</span>
      </div>
      {audioUrl ? (
        <audio className="w-full" controls src={audioUrl} />
      ) : (
        <button
          type="button"
          onClick={handleLoad}
          className="inline-flex items-center justify-center gap-2 rounded-md bg-slate-900 px-3 py-2 text-xs font-medium text-white transition hover:bg-slate-700 disabled:cursor-wait disabled:bg-slate-500"
          disabled={loading}
        >
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
          Audio laden
        </button>
      )}
      {error && <div className="text-red-600">{error}</div>}
    </div>
  );
};

const getMessageAudio = (message: DebugConversationMessage): DebugAudioMeta[] => {
  if (Array.isArray(message.debug_audio)) return message.debug_audio;
  if (message.debug_audio) return [message.debug_audio];
  if (Array.isArray(message.debug_audio_segments)) return message.debug_audio_segments;
  return [];
};

const getTranscriptionAudio = (message: DebugConversationMessage): DebugAudioMeta[] => {
  return message.role === 'user' ? getMessageAudio(message) : [];
};

const getPronunciationAudio = (message: DebugConversationMessage): DebugAudioMeta[] => {
  return message.role === 'assistant' ? getMessageAudio(message) : [];
};

const countAudioForRole = (messages: DebugConversationMessage[], role: DebugConversationMessage['role']): number => {
  return messages.reduce((total, message) => (
    message.role === role ? total + getMessageAudio(message).length : total
  ), 0);
};

const evaluationKey = (
  chatId: string,
  messageIndex: number,
  audioId: string,
  aspect: 'transcription' | 'pronunciation',
): string => `${chatId}:${messageIndex}:${audioId || 'text'}:${aspect}`;

const getProfileFromConversation = (conversation: DebugConversationDetail | null): { id?: string; name?: string } | null => {
  const debugProfile = conversation?.debug_metadata?.debug_profile;
  if (!debugProfile || typeof debugProfile !== 'object') {
    return null;
  }
  const id = typeof debugProfile.id === 'string' ? debugProfile.id : undefined;
  const name = typeof debugProfile.name === 'string' ? debugProfile.name : undefined;
  return id || name ? { id, name } : null;
};

const stringifyReplacementOverrides = (profile: DebugProfile | null): string => {
  const replacements = profile?.settings?.pronunciation?.replacement_overrides;
  if (!Array.isArray(replacements)) return '';
  return replacements
    .map((item) => `${item.word || ''}=${item.voice_replacement || ''}${item.note ? ` # ${item.note}` : ''}`.trim())
    .filter(Boolean)
    .join('\n');
};

type PronunciationReplacementOverride = {
  word: string;
  voice_replacement: string;
  note?: string;
};

type PronunciationPhraseTest = {
  id?: string;
  phrase: string;
  expected?: string;
  note?: string;
};

const parseReplacementOverrides = (text: string): PronunciationReplacementOverride[] => {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [body, note = ''] = line.split('#', 2);
      const [word, replacement] = body.split('=', 2);
      return {
        word: (word || '').trim(),
        voice_replacement: (replacement || '').trim(),
        note: note.trim(),
      };
    })
    .filter((item) => item.word && item.voice_replacement);
};

const stringifyPhraseTests = (profile: DebugProfile | null): string => {
  const phrases = profile?.settings?.pronunciation?.phrase_tests;
  if (!Array.isArray(phrases)) return '';
  return phrases
    .map((item) => `${item.phrase || ''}${item.expected ? ` => ${item.expected}` : ''}${item.note ? ` # ${item.note}` : ''}`.trim())
    .filter(Boolean)
    .join('\n');
};

const parsePhraseTests = (text: string): PronunciationPhraseTest[] => {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [body, note = ''] = line.split('#', 2);
      const [phrase, expected = ''] = body.split('=>', 2);
      return {
        phrase: (phrase || '').trim(),
        expected: expected.trim(),
        note: note.trim(),
      };
    })
    .filter((item) => item.phrase);
};

const collectSettingDiffs = (factory: DebugProfile | null, profile: DebugProfile | null): string[] => {
  if (!factory || !profile) return [];
  const diffs: string[] = [];
  const walk = (base: unknown, next: unknown, path: string) => {
    if (JSON.stringify(base) === JSON.stringify(next)) return;
    if (
      base &&
      next &&
      typeof base === 'object' &&
      typeof next === 'object' &&
      !Array.isArray(base) &&
      !Array.isArray(next)
    ) {
      const keys = new Set([...Object.keys(base), ...Object.keys(next)]);
      keys.forEach((key) => walk((base as any)[key], (next as any)[key], path ? `${path}.${key}` : key));
      return;
    }
    diffs.push(path);
  };
  walk(factory.settings, profile.settings, '');
  return diffs.filter(Boolean).slice(0, 24);
};

interface EvaluationEditorProps {
  chatId: string;
  message: DebugConversationMessage;
  messageIndex: number;
  audio?: DebugAudioMeta;
  aspect: 'transcription' | 'pronunciation';
  profileId?: string | null;
  evaluation?: DebugEvaluation;
  draft?: Partial<DebugEvaluation>;
  isDarkMode: boolean;
  onDraftChange: (key: string, draft: Partial<DebugEvaluation>) => void;
  onSave: (evaluation: DebugEvaluation) => Promise<void>;
}

const EvaluationEditor: React.FC<EvaluationEditorProps> = ({
  chatId,
  message,
  messageIndex,
  audio,
  aspect,
  profileId,
  evaluation,
  draft,
  isDarkMode,
  onDraftChange,
  onSave,
}) => {
  const key = evaluationKey(chatId, messageIndex, audio?.audio_id || '', aspect);
  const merged = { ...(evaluation || {}), ...(draft || {}) };
  const rating = typeof merged.rating === 'number' ? merged.rating : 0;

  const update = (next: Partial<DebugEvaluation>) => {
    onDraftChange(key, { ...draft, ...next });
  };

  return (
    <div className={`mt-3 rounded-md border px-3 py-3 text-xs ${
      isDarkMode ? 'border-slate-800 bg-slate-950/70 text-slate-300' : 'border-slate-200 bg-white text-slate-700'
    }`}>
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div className="font-semibold">
          {aspect === 'pronunciation' ? 'Aussprache bewerten' : 'Transkript bewerten'}
        </div>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => update({ rating: value })}
              className={`h-7 w-7 rounded border text-[11px] font-semibold transition ${
                rating === value
                  ? 'border-emerald-500 bg-emerald-500 text-slate-950'
                  : isDarkMode
                  ? 'border-slate-700 text-slate-400 hover:bg-slate-800'
                  : 'border-slate-200 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {value}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select
          value={merged.issue_type || ''}
          onChange={(event) => update({ issue_type: event.target.value })}
          className={`rounded-md border px-2 py-2 text-xs outline-none ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
          }`}
        >
          <option value="">Kein Problemtyp</option>
          <option value="noise">Störgeräusch</option>
          <option value="missing_words">Wörter fehlen</option>
          <option value="hallucination">Halluzination</option>
          <option value="wrong_pronunciation">Falsche Aussprache</option>
          <option value="intonation">Betonung/Intonation</option>
          <option value="too_fast">Zu schnell</option>
          <option value="cut_off">Abgeschnitten</option>
        </select>
        <input
          value={merged.expected_text || ''}
          onChange={(event) => update({ expected_text: event.target.value })}
          placeholder={aspect === 'pronunciation' ? 'Erwartete Aussprache' : 'Erwartetes Transkript'}
          className={`rounded-md border px-2 py-2 text-xs outline-none ${
            isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-600' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
          }`}
        />
      </div>
      <textarea
        value={merged.note || ''}
        onChange={(event) => update({ note: event.target.value })}
        placeholder="Notiz zur Bewertung"
        rows={2}
        className={`mt-2 w-full resize-y rounded-md border px-2 py-2 text-xs outline-none ${
          isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-600' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
        }`}
      />
      <button
        type="button"
        onClick={() => onSave({
          id: evaluation?.id,
          chat_id: chatId,
          message_index: messageIndex,
          role: message.role,
          audio_id: audio?.audio_id || '',
          aspect,
          profile_id: profileId || null,
          rating,
          issue_type: merged.issue_type || '',
          expected_text: merged.expected_text || '',
          selected_text: message.content || '',
          note: merged.note || '',
        })}
        className="mt-2 inline-flex items-center gap-2 rounded-md bg-emerald-500 px-3 py-2 text-xs font-semibold text-slate-950 transition hover:bg-emerald-400"
      >
        <Save className="h-3.5 w-3.5" />
        Bewertung speichern
      </button>
    </div>
  );
};

const DEBUG_REFRESH_RETRY_DELAYS_MS = [500, 1200, 2500];
const PRONUNCIATION_TEST_TIMEOUT_MS = 30000;
const PCM24K_SAMPLE_RATE = 24000;
const DEFAULT_PRONUNCIATION_TEST_PHRASE =
  'Willkommen bei Synaworks. Synaworks unterstützt SAP-IT-Organisationen.';
const PRONUNCIATION_LAB_PHRASE_STORAGE_KEY = 'debug.pronunciationLabPhrase';
const REALTIME_VOICE_OPTIONS = [
  'alloy',
  'ash',
  'ballad',
  'coral',
  'echo',
  'sage',
  'shimmer',
  'verse',
  'marin',
  'cedar',
];

type BrowserNetworkConnection = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  type?: string;
};

const EMPTY_VOCAB_WORD: VocabWord = {
  word: '',
  context: '',
  pronunciation: '',
  output_pronunciation: '',
  input_aliases: [],
  voice_replacement: '',
};

const vocabWordKey = (word?: string): string => (word || '').trim().toLowerCase();

const normalizeVocabularyDraft = (word: VocabWord | null): VocabWord => {
  const aliases = word && Array.isArray(word.input_aliases) ? word.input_aliases : [];
  return {
    ...EMPTY_VOCAB_WORD,
    ...(word || {}),
    word: word?.word || '',
    context: word?.context || '',
    pronunciation: word?.pronunciation || '',
    output_pronunciation: word?.output_pronunciation || word?.pronunciation || '',
    input_aliases: aliases,
    voice_replacement: word?.voice_replacement || '',
  };
};

const stringifyVocabularyAliases = (word: VocabWord | null): string => {
  const aliases = word && Array.isArray(word.input_aliases) ? word.input_aliases : [];
  return aliases.join('\n');
};

const parseVocabularyAliases = (text: string): string[] => (
  text
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)
);

type PronunciationLabRun = {
  id: string;
  chatId: string;
  phrase: string;
  spokenPhrase?: string;
  voice?: string;
  status: 'running' | 'done' | 'error';
  verdict?: 'pass' | 'fail' | 'unclear';
  replacementApplied?: boolean;
  durationMs?: number;
  audioChunkCount?: number;
  audioDurationMs?: number;
  transcript?: string;
  audioUrl?: string;
  error?: string;
  createdAt: string;
};

const getDebugRealtimeWsUrl = (): string => {
  if (process.env.REACT_APP_REALTIME_WS_URL) {
    return process.env.REACT_APP_REALTIME_WS_URL;
  }
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  const isSecure = window.location.protocol === 'https:';
  const isNgrok = host.includes('ngrok');
  const useProxy = process.env.REACT_APP_USE_PROXY === 'true';
  if (isSecure || isNgrok || useProxy) {
    return `${protocol}//${host}/realtime`;
  }
  const wsHost = window.location.hostname || 'localhost';
  return `${protocol}//${wsHost}:8766`;
};

const getDebugClientInfo = (): Record<string, unknown> => {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return {};
  }
  const extendedNavigator = navigator as Navigator & {
    connection?: BrowserNetworkConnection;
    mozConnection?: BrowserNetworkConnection;
    webkitConnection?: BrowserNetworkConnection;
    deviceMemory?: number;
  };
  const connection =
    extendedNavigator.connection ||
    extendedNavigator.mozConnection ||
    extendedNavigator.webkitConnection;

  return {
    source: 'pronunciation_lab',
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    languages: Array.from(navigator.languages || []),
    online: navigator.onLine,
    hardwareConcurrency: navigator.hardwareConcurrency,
    deviceMemory: extendedNavigator.deviceMemory,
    maxTouchPoints: navigator.maxTouchPoints,
    devicePixelRatio: window.devicePixelRatio,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    network: connection ? {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData,
      type: connection.type,
    } : null,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight,
    },
    screen: {
      width: window.screen?.width,
      height: window.screen?.height,
      availWidth: window.screen?.availWidth,
      availHeight: window.screen?.availHeight,
    },
  };
};

const base64ToUint8Array = (base64: string): Uint8Array => {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const buildWavBlobFromPcm24k = (chunks: string[]): Blob | null => {
  if (!chunks.length) {
    return null;
  }
  const pcmChunks = chunks.map(base64ToUint8Array);
  const pcmLength = pcmChunks.reduce((total, chunk) => total + chunk.byteLength, 0);
  if (pcmLength === 0) {
    return null;
  }

  const wav = new ArrayBuffer(44 + pcmLength);
  const view = new DataView(wav);
  const writeString = (offset: number, value: string) => {
    for (let i = 0; i < value.length; i += 1) {
      view.setUint8(offset + i, value.charCodeAt(i));
    }
  };

  writeString(0, 'RIFF');
  view.setUint32(4, 36 + pcmLength, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, PCM24K_SAMPLE_RATE, true);
  view.setUint32(28, PCM24K_SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeString(36, 'data');
  view.setUint32(40, pcmLength, true);

  let offset = 44;
  const target = new Uint8Array(wav);
  pcmChunks.forEach((chunk) => {
    target.set(chunk, offset);
    offset += chunk.byteLength;
  });

  return new Blob([wav], { type: 'audio/wav' });
};

const estimatePcm24kAudioDurationMs = (chunks: string[]): number => {
  const pcmLength = chunks.reduce((total, chunk) => total + base64ToUint8Array(chunk).byteLength, 0);
  return Math.round((pcmLength / (PCM24K_SAMPLE_RATE * 2)) * 1000);
};

const formatShortDuration = (ms?: number): string => {
  if (!ms || ms <= 0) return '-';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const DebugPage: React.FC<DebugPageProps> = ({ isDarkMode }) => {
  const {
    isAuthenticated,
    username,
    loading,
    error,
    login,
    logout,
    getDebugConversations,
    getDebugConversation,
    deleteDebugConversation,
    getDebugAudioUrl,
    getDebugProfiles,
    saveDebugProfile,
    cloneDebugProfile,
    deleteDebugProfile,
    getVocabulary,
    saveVocabulary,
    getDebugEvaluations,
    saveDebugEvaluation,
  } = useAdminApi();

  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [activeView, setActiveView] = useState<'talk' | 'debug'>('talk');
  const [debugModeView, setDebugModeView] = useState<'transcription' | 'pronunciation'>('transcription');
  const [conversations, setConversations] = useState<DebugConversationSummary[]>([]);
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null);
  const [selectedConversation, setSelectedConversation] = useState<DebugConversationDetail | null>(null);
  const [debugProfiles, setDebugProfiles] = useState<DebugProfile[]>([]);
  const [factoryProfileId, setFactoryProfileId] = useState('factory-default');
  const [selectedProfileId, setSelectedProfileId] = useState<string>('factory-default');
  const [profileNameDraft, setProfileNameDraft] = useState('');
  const [profileDescriptionDraft, setProfileDescriptionDraft] = useState('');
  const [profileTargetDraft, setProfileTargetDraft] = useState('balanced');
  const [profileStatusDraft, setProfileStatusDraft] = useState('draft');
  const [profileVoiceDraft, setProfileVoiceDraft] = useState('coral');
  const [replacementDraft, setReplacementDraft] = useState('');
  const [pronunciationNotesDraft, setPronunciationNotesDraft] = useState('');
  const [phraseTestsDraft, setPhraseTestsDraft] = useState('');
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [vocabWords, setVocabWords] = useState<VocabWord[]>([]);
  const [vocabLoading, setVocabLoading] = useState(false);
  const [selectedVocabularyWordKey, setSelectedVocabularyWordKey] = useState('');
  const [vocabularyDraft, setVocabularyDraft] = useState<VocabWord>(EMPTY_VOCAB_WORD);
  const [vocabularyAliasesDraft, setVocabularyAliasesDraft] = useState('');
  const [pronunciationTestPhraseDraft, setPronunciationTestPhraseDraft] = useState(() => {
    try {
      return window.localStorage.getItem(PRONUNCIATION_LAB_PHRASE_STORAGE_KEY) || DEFAULT_PRONUNCIATION_TEST_PHRASE;
    } catch (_) {
      return DEFAULT_PRONUNCIATION_TEST_PHRASE;
    }
  });
  const [pronunciationLabRunning, setPronunciationLabRunning] = useState(false);
  const [pronunciationLabMessage, setPronunciationLabMessage] = useState<string | null>(null);
  const [pronunciationRuns, setPronunciationRuns] = useState<PronunciationLabRun[]>([]);
  const [evaluations, setEvaluations] = useState<DebugEvaluation[]>([]);
  const [evaluationDrafts, setEvaluationDrafts] = useState<Record<string, Partial<DebugEvaluation>>>({});
  const [listLoading, setListLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deletingConversationIds, setDeletingConversationIds] = useState<Record<string, boolean>>({});
  const [deletingRunIds, setDeletingRunIds] = useState<Record<string, boolean>>({});
  const refreshRetryTimersRef = useRef<number[]>([]);
  const pronunciationAudioUrlsRef = useRef<string[]>([]);
  const pronunciationTestPhraseTouchedRef = useRef(false);

  const selectedSummary = useMemo(
    () => conversations.find((conversation) => conversation.chat_id === selectedChatId) || null,
    [conversations, selectedChatId],
  );
  const selectedUserAudioCount = useMemo(
    () => selectedConversation ? countAudioForRole(selectedConversation.messages, 'user') : 0,
    [selectedConversation],
  );
  const selectedAssistantAudioCount = useMemo(
    () => selectedConversation ? countAudioForRole(selectedConversation.messages, 'assistant') : 0,
    [selectedConversation],
  );
  const selectedProfile = useMemo(
    () => debugProfiles.find((profile) => profile.id === selectedProfileId) || debugProfiles[0] || null,
    [debugProfiles, selectedProfileId],
  );
  const selectedVocabularyWord = useMemo(
    () => vocabWords.find((word) => vocabWordKey(word.word) === selectedVocabularyWordKey) || null,
    [vocabWords, selectedVocabularyWordKey],
  );
  const debugReplacementOverrides = useMemo(
    () => parseReplacementOverrides(replacementDraft),
    [replacementDraft],
  );
  const activeVocabularyWordName = (
    vocabularyDraft.word.trim() ||
    selectedVocabularyWord?.word ||
    'Vocabulary-Wort'
  );
  const selectedVocabularyDebugOverride = useMemo(
    () => debugReplacementOverrides.find((item) => (
      vocabWordKey(item.word) === vocabWordKey(activeVocabularyWordName)
    )) || null,
    [activeVocabularyWordName, debugReplacementOverrides],
  );
  const effectiveVocabularyReplacement =
    selectedVocabularyDebugOverride?.voice_replacement ||
    vocabularyDraft.voice_replacement ||
    selectedVocabularyWord?.voice_replacement ||
    '';
  const effectiveVocabularySource = selectedVocabularyDebugOverride
    ? 'Debug-Profil Override'
    : vocabularyDraft.voice_replacement || selectedVocabularyWord?.voice_replacement
    ? 'Global Vocabulary'
    : 'kein Replacement aktiv';
  const factoryProfile = useMemo(
    () => debugProfiles.find((profile) => profile.id === factoryProfileId) || null,
    [debugProfiles, factoryProfileId],
  );
  const selectedConversationProfile = useMemo(
    () => getProfileFromConversation(selectedConversation),
    [selectedConversation],
  );
  const pronunciationLabStats = useMemo(() => {
    return pronunciationRuns.reduce(
      (stats, run) => {
        stats.total += 1;
        if (run.status === 'done') stats.done += 1;
        if (run.status === 'error') stats.error += 1;
        if (run.replacementApplied) stats.replaced += 1;
        if (run.verdict === 'pass') stats.pass += 1;
        if (run.verdict === 'fail') stats.fail += 1;
        if (run.verdict === 'unclear') stats.unclear += 1;
        return stats;
      },
      { total: 0, done: 0, error: 0, replaced: 0, pass: 0, fail: 0, unclear: 0 },
    );
  }, [pronunciationRuns]);
  const profileDiffs = useMemo(
    () => collectSettingDiffs(factoryProfile, selectedProfile),
    [factoryProfile, selectedProfile],
  );

  const refreshConversations = useCallback(async (preferredChatId?: string) => {
    if (!isAuthenticated) return;
    setListLoading(true);
    const result = await getDebugConversations();
    const nextConversations = result || [];
    setConversations(nextConversations);
    const nextSelected =
      preferredChatId ||
      selectedChatId ||
      nextConversations[0]?.chat_id ||
      null;
    setSelectedChatId(
      nextSelected && nextConversations.some((conversation) => conversation.chat_id === nextSelected)
        ? nextSelected
        : nextConversations[0]?.chat_id || null,
    );
    setListLoading(false);
  }, [getDebugConversations, isAuthenticated, selectedChatId]);

  const refreshProfiles = useCallback(async (preferredProfileId?: string) => {
    if (!isAuthenticated) return;
    setProfileLoading(true);
    const result = await getDebugProfiles();
    const nextProfiles = result?.profiles || [];
    setDebugProfiles(nextProfiles);
    if (result?.factory_profile_id) {
      setFactoryProfileId(result.factory_profile_id);
    }
    const nextSelected =
      preferredProfileId ||
      selectedProfileId ||
      result?.factory_profile_id ||
      nextProfiles[0]?.id ||
      'factory-default';
    setSelectedProfileId(
      nextProfiles.some((profile) => profile.id === nextSelected)
        ? nextSelected
        : result?.factory_profile_id || nextProfiles[0]?.id || 'factory-default',
    );
    setProfileLoading(false);
  }, [getDebugProfiles, isAuthenticated, selectedProfileId]);

  const refreshVocabulary = useCallback(async () => {
    if (!isAuthenticated) return;
    setVocabLoading(true);
    const result = await getVocabulary();
    const words = result?.words || [];
    setVocabWords(words);
    setSelectedVocabularyWordKey((current) => (
      words.some((word) => vocabWordKey(word.word) === current)
        ? current
        : vocabWordKey(words[0]?.word)
    ));
    setVocabLoading(false);
  }, [getVocabulary, isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated) return;
    void refreshConversations();
    void refreshProfiles();
    void refreshVocabulary();
  }, [isAuthenticated, refreshConversations, refreshProfiles, refreshVocabulary]);

  useEffect(() => {
    if (!selectedProfile) return;
    setProfileNameDraft(selectedProfile.name || '');
    setProfileDescriptionDraft(selectedProfile.description || '');
    setProfileTargetDraft(selectedProfile.target || 'balanced');
    setProfileStatusDraft(selectedProfile.status || 'draft');
    setProfileVoiceDraft(selectedProfile.settings?.pronunciation?.voice || 'coral');
    setReplacementDraft(stringifyReplacementOverrides(selectedProfile));
    setPronunciationNotesDraft(selectedProfile.settings?.pronunciation?.prompt_notes || '');
    setPhraseTestsDraft(stringifyPhraseTests(selectedProfile));
    const firstPhrase = selectedProfile.settings?.pronunciation?.phrase_tests?.[0]?.phrase;
    setPronunciationTestPhraseDraft((current) => {
      if (pronunciationTestPhraseTouchedRef.current) {
        return current;
      }
      if (current && current !== DEFAULT_PRONUNCIATION_TEST_PHRASE) {
        return current;
      }
      return firstPhrase || current || DEFAULT_PRONUNCIATION_TEST_PHRASE;
    });
    setProfileMessage(null);
  }, [selectedProfile]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PRONUNCIATION_LAB_PHRASE_STORAGE_KEY, pronunciationTestPhraseDraft);
    } catch (_) {
      // Local persistence is only a debug convenience.
    }
  }, [pronunciationTestPhraseDraft]);

  useEffect(() => {
    const nextDraft = normalizeVocabularyDraft(selectedVocabularyWord);
    setVocabularyDraft(nextDraft);
    setVocabularyAliasesDraft(stringifyVocabularyAliases(selectedVocabularyWord));
  }, [selectedVocabularyWord]);

  useEffect(() => {
    return () => {
      refreshRetryTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
      refreshRetryTimersRef.current = [];
      pronunciationAudioUrlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      pronunciationAudioUrlsRef.current = [];
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !selectedChatId) {
      setSelectedConversation(null);
      setEvaluations([]);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    Promise.all([
      getDebugConversation(selectedChatId),
      getDebugEvaluations(selectedChatId),
    ]).then(([conversation, nextEvaluations]) => {
      if (cancelled) return;
      setSelectedConversation(conversation);
      setEvaluations(nextEvaluations || []);
      setEvaluationDrafts({});
      setDetailLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [getDebugConversation, getDebugEvaluations, isAuthenticated, selectedChatId]);

  const handleLogin = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    await login(loginUser, loginPass);
  };

  const refreshClosedVoiceConversation = useCallback((voiceChatId?: string) => {
    refreshRetryTimersRef.current.forEach((timerId) => window.clearTimeout(timerId));
    refreshRetryTimersRef.current = [];
    void refreshConversations(voiceChatId);

    refreshRetryTimersRef.current = DEBUG_REFRESH_RETRY_DELAYS_MS.map((delayMs) =>
      window.setTimeout(() => {
        void refreshConversations(voiceChatId);
      }, delayMs),
    );
  }, [refreshConversations]);

  const handleVoiceClose = (_messages?: unknown, voiceChatId?: string) => {
    setVoiceOpen(false);
    setActiveView('debug');
    refreshClosedVoiceConversation(voiceChatId);
  };

  const handleDeleteDebugConversation = async (chatId: string) => {
    const confirmed = window.confirm('Dieses Debug-Gespräch inklusive Debug-Audio und Bewertungen wirklich löschen?');
    if (!confirmed) return;
    setDeletingConversationIds((prev) => ({ ...prev, [chatId]: true }));
    const ok = await deleteDebugConversation(chatId);
    if (ok) {
      setConversations((prev) => prev.filter((conversation) => conversation.chat_id !== chatId));
      if (selectedChatId === chatId) {
        setSelectedChatId(null);
        setSelectedConversation(null);
        setEvaluations([]);
        setEvaluationDrafts({});
      }
      setPronunciationRuns((prev) => {
        prev.forEach((run) => {
          if (run.chatId === chatId && run.audioUrl) {
            URL.revokeObjectURL(run.audioUrl);
            pronunciationAudioUrlsRef.current = pronunciationAudioUrlsRef.current.filter((url) => url !== run.audioUrl);
          }
        });
        return prev.filter((run) => run.chatId !== chatId);
      });
      await refreshConversations();
    }
    setDeletingConversationIds((prev) => {
      const next = { ...prev };
      delete next[chatId];
      return next;
    });
  };

  const handleDeletePronunciationRun = async (run: PronunciationLabRun) => {
    const confirmed = window.confirm('Diesen Pronunciation-Testlauf inklusive Debug-Gespräch löschen?');
    if (!confirmed) return;
    setDeletingRunIds((prev) => ({ ...prev, [run.id]: true }));
    const ok = await deleteDebugConversation(run.chatId);
    if (run.audioUrl) {
      URL.revokeObjectURL(run.audioUrl);
      pronunciationAudioUrlsRef.current = pronunciationAudioUrlsRef.current.filter((url) => url !== run.audioUrl);
    }
    if (ok) {
      setPronunciationRuns((prev) => prev.filter((item) => item.id !== run.id));
      setConversations((prev) => prev.filter((conversation) => conversation.chat_id !== run.chatId));
      if (selectedChatId === run.chatId) {
        setSelectedChatId(null);
        setSelectedConversation(null);
        setEvaluations([]);
        setEvaluationDrafts({});
      }
      await refreshConversations();
    } else {
      setPronunciationRuns((prev) => prev.filter((item) => item.id !== run.id));
      setPronunciationLabMessage('Testlauf wurde lokal entfernt, das Debug-Gespräch konnte aber nicht gelöscht werden.');
    }
    setDeletingRunIds((prev) => {
      const next = { ...prev };
      delete next[run.id];
      return next;
    });
  };

  const handleCreateVocabularyWord = () => {
    setSelectedVocabularyWordKey('');
    setVocabularyDraft(EMPTY_VOCAB_WORD);
    setVocabularyAliasesDraft('');
    setProfileMessage('Neues Vocabulary-Wort vorbereitet. Speichern ändert das Production-Vocabulary.');
  };

  const handleSaveVocabularyDraft = async () => {
    const wordName = vocabularyDraft.word.trim();
    if (!wordName) {
      setProfileMessage('Bitte zuerst ein Vocabulary-Wort eintragen.');
      return;
    }

    const confirmed = window.confirm(
      [
        'Diese Änderung betrifft das globale Vocabulary und damit auch den Production-Modus von Sally.',
        '',
        'Für reine Aussprachetests bitte stattdessen den Debug-Override verwenden.',
        '',
        `Globales Vocabulary für "${wordName}" wirklich speichern?`,
      ].join('\n'),
    );
    if (!confirmed) {
      setProfileMessage('Globales Vocabulary wurde nicht geändert.');
      return;
    }

    setVocabLoading(true);
    const originalKey = selectedVocabularyWordKey;
    const nextKey = vocabWordKey(wordName);
    const originalIndex = vocabWords.findIndex((word) => vocabWordKey(word.word) === originalKey);
    const existingIndex = vocabWords.findIndex((word) => vocabWordKey(word.word) === nextKey);
    const sourceIndex = originalIndex >= 0 ? originalIndex : existingIndex;
    const sourceWord = sourceIndex >= 0 ? vocabWords[sourceIndex] : EMPTY_VOCAB_WORD;
    const nextWord: VocabWord = {
      ...sourceWord,
      word: wordName,
      context: vocabularyDraft.context.trim(),
      pronunciation: vocabularyDraft.pronunciation.trim(),
      output_pronunciation: (vocabularyDraft.output_pronunciation || vocabularyDraft.pronunciation).trim(),
      input_aliases: parseVocabularyAliases(vocabularyAliasesDraft),
      voice_replacement: (vocabularyDraft.voice_replacement || '').trim(),
    };
    const remainingWords = vocabWords.filter((word) => {
      const key = vocabWordKey(word.word);
      return key !== originalKey && key !== nextKey;
    });
    const insertIndex = sourceIndex >= 0 ? Math.min(sourceIndex, remainingWords.length) : 0;
    const dedupedWords = [
      ...remainingWords.slice(0, insertIndex),
      nextWord,
      ...remainingWords.slice(insertIndex),
    ];

    const ok = await saveVocabulary(dedupedWords);
    if (ok) {
      setVocabWords(dedupedWords);
      setSelectedVocabularyWordKey(nextKey);
      setProfileMessage(`Globales Vocabulary für "${wordName}" wurde gespeichert.`);
    } else {
      setProfileMessage(`Globales Vocabulary für "${wordName}" konnte nicht gespeichert werden.`);
    }
    setVocabLoading(false);
  };

  const handleSetVocabularyDebugOverride = async () => {
    if (!selectedProfile) {
      setProfileMessage('Kein Debug-Profil ausgewählt.');
      return;
    }
    if (selectedProfile.locked || selectedProfile.factory) {
      setProfileMessage('Die Werkeinstellung ist geschützt. Bitte zuerst ein neues Profil anlegen oder klonen.');
      return;
    }
    const wordName = activeVocabularyWordName.trim();
    if (!wordName || wordName === 'Vocabulary-Wort') {
      setProfileMessage('Bitte zuerst ein Vocabulary-Wort auswählen oder anlegen.');
      return;
    }
    const replacement = (
      (vocabularyDraft.voice_replacement || '').trim() ||
      effectiveVocabularyReplacement ||
      wordName
    );
    const lines = replacementDraft.split(/\r?\n/);
    let updatedExistingLine = false;
    const nextLines = lines.map((line) => {
      const match = line.match(/^\s*([^=#]+?)\s*=/);
      if (match && vocabWordKey(match[1]) === vocabWordKey(wordName)) {
        updatedExistingLine = true;
        return `${wordName}=${replacement}`;
      }
      return line;
    });
    if (!updatedExistingLine) {
      nextLines.push(`${wordName}=${replacement}`);
    }
    const nextReplacementDraft = nextLines.join('\n').trim();
    setReplacementDraft(nextReplacementDraft);
    setProfileLoading(true);
    const nextProfile: DebugProfile = {
      ...selectedProfile,
      name: profileNameDraft.trim() || selectedProfile.name,
      description: profileDescriptionDraft.trim(),
      target: profileTargetDraft,
      status: profileStatusDraft,
      settings: {
        ...(selectedProfile.settings || {}),
        pronunciation: {
          ...(selectedProfile.settings?.pronunciation || {}),
          voice: profileVoiceDraft,
          replacement_overrides: parseReplacementOverrides(nextReplacementDraft),
          prompt_notes: pronunciationNotesDraft,
          phrase_tests: parsePhraseTests(phraseTestsDraft),
        },
      },
    };
    try {
      const saved = await saveDebugProfile(nextProfile);
      if (saved) {
        await refreshProfiles(saved.id);
        setProfileMessage(`Debug-Override für "${wordName}" wurde im Profil gespeichert.`);
      } else {
        setProfileMessage(`Debug-Override für "${wordName}" konnte nicht gespeichert werden.`);
      }
    } catch (_) {
      setProfileMessage(`Debug-Override für "${wordName}" konnte nicht gespeichert werden.`);
    } finally {
      setProfileLoading(false);
    }
  };

  const updatePronunciationRun = (runId: string, patch: Partial<PronunciationLabRun>) => {
    setPronunciationRuns((prev) => prev.map((run) => (
      run.id === runId ? { ...run, ...patch } : run
    )));
  };

  const setPronunciationRunVerdict = (runId: string, verdict: PronunciationLabRun['verdict']) => {
    setPronunciationRuns((prev) => prev.map((run) => (
      run.id === runId
        ? { ...run, verdict: run.verdict === verdict ? undefined : verdict }
        : run
    )));
  };

  const runSinglePronunciationTest = useCallback((phrase: string, repetitionIndex: number): Promise<string | null> => {
    if (!selectedProfile) {
      return Promise.resolve(null);
    }
    const debugProfileSnapshot: DebugProfile = {
      ...selectedProfile,
      name: profileNameDraft.trim() || selectedProfile.name,
      description: profileDescriptionDraft.trim(),
      target: profileTargetDraft,
      status: profileStatusDraft,
      settings: {
        ...(selectedProfile.settings || {}),
        pronunciation: {
          ...(selectedProfile.settings?.pronunciation || {}),
          voice: profileVoiceDraft,
          replacement_overrides: parseReplacementOverrides(replacementDraft),
          prompt_notes: pronunciationNotesDraft,
          phrase_tests: parsePhraseTests(phraseTestsDraft),
        },
      },
    };
    const runId = `pron_${Date.now()}_${repetitionIndex}_${Math.random().toString(36).slice(2, 8)}`;
    const chatId = `pronunciation_lab_${runId}`;
    const startedAt = new Date().toISOString();
    const startedAtMs = performance.now();
    const chunks: string[] = [];
    const pendingRun: PronunciationLabRun = {
      id: runId,
      chatId,
      phrase,
      status: 'running',
      createdAt: startedAt,
    };

    setPronunciationRuns((prev) => [pendingRun, ...prev].slice(0, 30));

    return new Promise((resolve) => {
      let settled = false;
      let ws: WebSocket | null = null;
      const finish = (chatIdResult: string | null, patch?: Partial<PronunciationLabRun>) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        updatePronunciationRun(runId, {
          durationMs: Math.round(performance.now() - startedAtMs),
          ...(patch || {}),
        });
        try {
          if (ws?.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: 'realtime.end' }));
          }
          ws?.close();
        } catch (_) {
          // ignore close errors
        }
        resolve(chatIdResult);
      };

      const timeoutId = window.setTimeout(() => {
        finish(null, {
          status: 'error',
          error: 'Pronunciation-Test Timeout',
        });
      }, PRONUNCIATION_TEST_TIMEOUT_MS);

      try {
        ws = new WebSocket(getDebugRealtimeWsUrl());
      } catch (error) {
        finish(null, {
          status: 'error',
          error: error instanceof Error ? error.message : 'WebSocket konnte nicht geöffnet werden',
        });
        return;
      }

      ws.onopen = () => {
        const adminToken = sessionStorage.getItem('admin_token');
        ws?.send(JSON.stringify({
          type: 'realtime.start',
          chat_id: chatId,
          ...(adminToken ? { auth_token: adminToken } : {}),
          debug_mode: true,
          client_info: getDebugClientInfo(),
          debug_profile_id: selectedProfile.id,
          debug_profile_snapshot: debugProfileSnapshot,
        }));
      };

      ws.onmessage = (event) => {
        let data: any;
        try {
          data = JSON.parse(event.data);
        } catch (_) {
          return;
        }

        if (data.type === 'realtime.started') {
          ws?.send(JSON.stringify({
            type: 'realtime.debug_pronunciation_test',
            test_id: runId,
            phrase,
          }));
          return;
        }

        if (data.type === 'realtime.debug_pronunciation_test.started') {
          const spokenPhrase = typeof data.spoken_phrase === 'string' ? data.spoken_phrase : undefined;
          const displayPhrase = typeof data.phrase === 'string' ? data.phrase : phrase;
          updatePronunciationRun(runId, {
            spokenPhrase,
            voice: typeof data.voice === 'string' ? data.voice : undefined,
            replacementApplied: Boolean(spokenPhrase && displayPhrase && spokenPhrase !== displayPhrase),
          });
          return;
        }

        if (data.type === 'realtime.audio' && typeof data.audio === 'string') {
          chunks.push(data.audio);
          return;
        }

        if (data.type === 'realtime.debug_pronunciation_test.done') {
          const wavBlob = buildWavBlobFromPcm24k(chunks);
          const audioUrl = wavBlob ? URL.createObjectURL(wavBlob) : undefined;
          if (audioUrl) {
            pronunciationAudioUrlsRef.current.push(audioUrl);
          }
          finish(chatId, {
            status: 'done',
            spokenPhrase: typeof data.spoken_phrase === 'string' ? data.spoken_phrase : undefined,
            voice: typeof data.voice === 'string' ? data.voice : undefined,
            replacementApplied: Boolean(
              typeof data.spoken_phrase === 'string' &&
              data.spoken_phrase &&
              data.spoken_phrase !== phrase,
            ),
            audioChunkCount: chunks.length,
            audioDurationMs: estimatePcm24kAudioDurationMs(chunks),
            transcript: typeof data.transcript === 'string' ? data.transcript : undefined,
            audioUrl,
          });
          return;
        }

        if (data.type === 'realtime.debug_pronunciation_test.error' || data.type === 'realtime.error') {
          finish(null, {
            status: 'error',
            error: typeof data.message === 'string' ? data.message : 'Realtime-Test fehlgeschlagen',
          });
        }
      };

      ws.onerror = () => {
        finish(null, {
          status: 'error',
          error: 'WebSocket-Fehler im Pronunciation-Test',
        });
      };

      ws.onclose = () => {
        if (!settled) {
          finish(null, {
            status: 'error',
            error: 'WebSocket wurde vor Testende geschlossen',
          });
        }
      };
    });
  }, [
    selectedProfile,
    profileNameDraft,
    profileDescriptionDraft,
    profileTargetDraft,
    profileStatusDraft,
    profileVoiceDraft,
    replacementDraft,
    pronunciationNotesDraft,
    phraseTestsDraft,
  ]);

  const handleRunPronunciationTests = async (repetitions: number) => {
    const phrase = pronunciationTestPhraseDraft.trim();
    if (!phrase) {
      setPronunciationLabMessage('Bitte zuerst eine Testphrase eintragen.');
      return;
    }
    if (!selectedProfile) {
      setPronunciationLabMessage('Kein Debug-Profil ausgewählt.');
      return;
    }
    setPronunciationLabRunning(true);
    setPronunciationLabMessage(null);
    let lastChatId: string | null = null;
    for (let index = 0; index < repetitions; index += 1) {
      // Sequential runs keep OpenAI/LiveAvatar timing comparable and avoid
      // overlapping realtime responses in the same browser.
      lastChatId = await runSinglePronunciationTest(phrase, index + 1);
    }
    setPronunciationLabRunning(false);
    setActiveView('debug');
    setDebugModeView('pronunciation');
    if (lastChatId) {
      refreshClosedVoiceConversation(lastChatId);
    } else {
      void refreshConversations();
    }
  };

  const handleCloneSelectedProfile = async () => {
    if (!selectedProfile) return;
    setProfileMessage(null);
    const cloned = await cloneDebugProfile(selectedProfile.id, `Kopie von ${selectedProfile.name}`);
    if (cloned) {
      await refreshProfiles(cloned.id);
      setProfileMessage('Profil wurde geklont.');
    } else {
      setProfileMessage('Profil konnte nicht geklont werden.');
    }
  };

  const handleCreatePronunciationProfile = async () => {
    const sourceProfile = selectedProfile || factoryProfile;
    if (!sourceProfile) return;
    setProfileMessage(null);
    const cloned = await cloneDebugProfile(sourceProfile.id, 'Neues Aussprache-Profil');
    if (!cloned) {
      setProfileMessage('Aussprache-Profil konnte nicht angelegt werden.');
      return;
    }
    const saved = await saveDebugProfile({
      ...cloned,
      target: 'pronunciation',
      status: 'draft',
    });
    await refreshProfiles(saved?.id || cloned.id);
    setDebugModeView('pronunciation');
    setProfileMessage('Neues Aussprache-Profil wurde angelegt und kann bearbeitet werden.');
  };

  const buildProfileFromDrafts = useCallback((settingsOverride?: DebugProfile['settings']): DebugProfile | null => {
    if (!selectedProfile) return null;
    const settings = settingsOverride || {
      ...(selectedProfile.settings || {}),
      pronunciation: {
        ...(selectedProfile.settings?.pronunciation || {}),
        voice: profileVoiceDraft,
        replacement_overrides: parseReplacementOverrides(replacementDraft),
        prompt_notes: pronunciationNotesDraft,
        phrase_tests: parsePhraseTests(phraseTestsDraft),
      },
    };
    return {
      ...selectedProfile,
      name: profileNameDraft.trim() || selectedProfile.name,
      description: profileDescriptionDraft.trim(),
      target: profileTargetDraft,
      status: profileStatusDraft,
      settings,
    };
  }, [
    selectedProfile,
    profileNameDraft,
    profileDescriptionDraft,
    profileTargetDraft,
    profileStatusDraft,
    profileVoiceDraft,
    replacementDraft,
    pronunciationNotesDraft,
    phraseTestsDraft,
  ]);

  const handleSaveProfileDraft = async () => {
    if (!selectedProfile) return;
    if (selectedProfile.locked || selectedProfile.factory) {
      setProfileMessage('Die Werkeinstellung ist geschützt. Bitte zuerst klonen.');
      return;
    }
    const nextProfile = buildProfileFromDrafts();
    if (!nextProfile) return;
    const saved = await saveDebugProfile(nextProfile);
    if (saved) {
      await refreshProfiles(saved.id);
      setProfileMessage('Profil wurde gespeichert.');
    } else {
      setProfileMessage('Profil konnte nicht gespeichert werden.');
    }
  };

  const handleDeleteSelectedProfile = async () => {
    if (!selectedProfile || selectedProfile.locked || selectedProfile.factory) return;
    const ok = await deleteDebugProfile(selectedProfile.id);
    if (ok) {
      await refreshProfiles(factoryProfileId);
      setProfileMessage('Profil wurde gelöscht.');
    } else {
      setProfileMessage('Profil konnte nicht gelöscht werden.');
    }
  };

  const handleSaveRuntimeProfileSettings = async (settings: VoiceDebugSettings): Promise<boolean> => {
    if (!selectedProfile || selectedProfile.locked || selectedProfile.factory) {
      setProfileMessage('Die Werkeinstellung ist geschützt. Bitte zuerst klonen.');
      return false;
    }
    const nextProfile = buildProfileFromDrafts({
      ...(selectedProfile.settings || {}),
      ...settings,
      pronunciation: {
        ...(selectedProfile.settings?.pronunciation || {}),
        voice: profileVoiceDraft,
        replacement_overrides: parseReplacementOverrides(replacementDraft),
        prompt_notes: pronunciationNotesDraft,
        phrase_tests: parsePhraseTests(phraseTestsDraft),
      },
    });
    if (!nextProfile) return false;
    const saved = await saveDebugProfile(nextProfile);
    if (!saved) {
      setProfileMessage('Runtime-Einstellungen konnten nicht gespeichert werden.');
      return false;
    }
    await refreshProfiles(saved.id);
    setProfileMessage('Runtime-Einstellungen wurden im Profil gespeichert.');
    return true;
  };

  const findEvaluation = useCallback((
    messageIndex: number,
    audioId: string,
    aspect: 'transcription' | 'pronunciation',
  ): DebugEvaluation | undefined => {
    if (!selectedConversation) return undefined;
    return evaluations.find((evaluation) => (
      evaluation.chat_id === selectedConversation.chat_id &&
      evaluation.message_index === messageIndex &&
      (evaluation.audio_id || '') === (audioId || '') &&
      evaluation.aspect === aspect
    ));
  }, [evaluations, selectedConversation]);

  const handleEvaluationDraftChange = (key: string, draft: Partial<DebugEvaluation>) => {
    setEvaluationDrafts((prev) => ({ ...prev, [key]: draft }));
  };

  const handleSaveEvaluation = async (evaluation: DebugEvaluation) => {
    const saved = await saveDebugEvaluation(evaluation);
    if (!saved) return;
    setEvaluations((prev) => {
      const withoutExisting = prev.filter((item) => item.id !== saved.id);
      return [...withoutExisting, saved];
    });
    const key = evaluationKey(saved.chat_id, saved.message_index, saved.audio_id || '', saved.aspect as 'transcription' | 'pronunciation');
    setEvaluationDrafts((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  if (!isAuthenticated) {
    return (
      <main className="min-h-screen bg-slate-950 px-4 py-10 text-slate-100">
        <div className="mx-auto flex min-h-[calc(100vh-5rem)] max-w-md items-center">
          <form onSubmit={handleLogin} className="w-full rounded-lg border border-slate-800 bg-slate-900 p-6 shadow-2xl">
            <div className="mb-6 flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-300">
                <Bug className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">Voice Debug</h1>
                <p className="text-sm text-slate-400">Admin-Login erforderlich</p>
              </div>
            </div>
            <label className="mb-4 block">
              <span className="mb-1.5 block text-sm text-slate-300">Username</span>
              <input
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none transition focus:border-emerald-400"
                value={loginUser}
                onChange={(event) => setLoginUser(event.target.value)}
                autoComplete="username"
                required
              />
            </label>
            <label className="mb-4 block">
              <span className="mb-1.5 block text-sm text-slate-300">Passwort</span>
              <input
                className="w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white outline-none transition focus:border-emerald-400"
                value={loginPass}
                onChange={(event) => setLoginPass(event.target.value)}
                autoComplete="current-password"
                type="password"
                required
              />
            </label>
            {error && <div className="mb-4 rounded-md bg-red-500/15 px-3 py-2 text-sm text-red-200">{error}</div>}
            <button
              type="submit"
              disabled={loading || !loginUser || !loginPass}
              className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Einloggen
            </button>
          </form>
        </div>
      </main>
    );
  }

  return (
    <main className={`min-h-screen ${isDarkMode ? 'bg-slate-950 text-slate-100' : 'bg-slate-100 text-slate-950'}`}>
      <header className={`border-b px-4 py-3 ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white'}`}>
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-emerald-500/15 text-emerald-500">
              <Bug className="h-5 w-5" />
            </div>
            <div className="min-w-0">
              <h1 className="truncate text-lg font-semibold">Voice Debug</h1>
              <p className={`truncate text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                Sandbox-Avatar, normale Voice-Einstellungen, gespeicherte User- und Sally-Audio-Snippets
              </p>
            </div>
          </div>
          <div className="flex min-w-0 items-center gap-2">
            <select
              value={selectedProfile?.id || selectedProfileId}
              onChange={(event) => setSelectedProfileId(event.target.value)}
              className={`max-w-[220px] rounded-md border px-3 py-2 text-sm outline-none ${
                isDarkMode
                  ? 'border-slate-700 bg-slate-950 text-slate-100'
                  : 'border-slate-300 bg-white text-slate-900'
              }`}
              title="Debug-Profil"
            >
              {debugProfiles.map((profile) => (
                <option key={profile.id} value={profile.id}>
                  {profile.name}{profile.locked ? ' (read-only)' : ''}
                </option>
              ))}
            </select>
            <span className={`hidden truncate text-sm sm:block ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{username}</span>
            <button
              type="button"
              onClick={() => setVoiceOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400"
            >
              <Mic className="h-4 w-4" />
              Voice Talk starten
            </button>
            <button
              type="button"
              onClick={() => {
                setActiveView('debug');
                void refreshConversations();
              }}
              className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition ${
                isDarkMode
                  ? 'border-slate-700 text-slate-200 hover:bg-slate-800'
                  : 'border-slate-300 text-slate-700 hover:bg-slate-50'
              }`}
            >
              <SlidersHorizontal className="h-4 w-4" />
              Debug-Auswertung öffnen
            </button>
            <button
              type="button"
              onClick={logout}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-md border transition ${
                isDarkMode
                  ? 'border-slate-700 text-slate-400 hover:bg-slate-800'
                  : 'border-slate-300 text-slate-500 hover:bg-slate-50'
              }`}
              aria-label="Abmelden"
              title="Abmelden"
            >
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 pt-4">
        <div className={`rounded-lg border p-2 ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white'}`}>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <button
              type="button"
              onClick={() => {
                setDebugModeView('transcription');
                setActiveView('debug');
              }}
              className={`flex min-w-0 items-center gap-3 rounded-md border px-4 py-3 text-left transition ${
                debugModeView === 'transcription'
                  ? 'border-emerald-400 bg-emerald-500/10'
                  : isDarkMode
                  ? 'border-slate-800 hover:border-slate-700 hover:bg-slate-800'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                debugModeView === 'transcription' ? 'bg-emerald-500 text-slate-950' : isDarkMode ? 'bg-slate-950 text-slate-400' : 'bg-slate-100 text-slate-500'
              }`}>
                <FileText className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  Transkript
                  <span className="rounded bg-black/10 px-1.5 py-0.5 text-[10px]">{selectedUserAudioCount}</span>
                </span>
                <span className={`block truncate text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  User-Audio, STT und Gesprächsverlauf bewerten
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={() => {
                setDebugModeView('pronunciation');
                setActiveView('debug');
              }}
              className={`flex min-w-0 items-center gap-3 rounded-md border px-4 py-3 text-left transition ${
                debugModeView === 'pronunciation'
                  ? 'border-orange-400 bg-orange-500/10'
                  : isDarkMode
                  ? 'border-slate-800 hover:border-slate-700 hover:bg-slate-800'
                  : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${
                debugModeView === 'pronunciation' ? 'bg-orange-500 text-white' : isDarkMode ? 'bg-slate-950 text-slate-400' : 'bg-slate-100 text-slate-500'
              }`}>
                <Volume2 className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2 text-sm font-semibold">
                  Aussprache
                  <span className="rounded bg-black/10 px-1.5 py-0.5 text-[10px]">{selectedAssistantAudioCount}</span>
                </span>
                <span className={`block truncate text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Sally-Audio, Profile, Pronunciation Lab und Vocabulary
                </span>
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-4 px-4 py-4 lg:grid-cols-[340px_minmax(0,1fr)]">
        <aside className={`min-h-[320px] rounded-lg border ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white'}`}>
          <div className={`flex items-center justify-between border-b px-4 py-3 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
            <div>
              <h2 className="text-sm font-semibold">Debug-Gespräche</h2>
              <p className={`text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>{conversations.length} Sessions</p>
            </div>
            <button
              type="button"
              onClick={() => refreshConversations()}
              className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition ${
                isDarkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-slate-200 hover:bg-slate-50'
              }`}
              aria-label="Aktualisieren"
              title="Aktualisieren"
            >
              <RefreshCw className={`h-4 w-4 ${listLoading ? 'animate-spin' : ''}`} />
            </button>
          </div>
          <div className="max-h-[calc(100vh-190px)] overflow-y-auto p-2">
            {conversations.length === 0 && (
              <div className={`px-3 py-8 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                Noch keine Debug-Gespräche.
              </div>
            )}
            {conversations.map((conversation) => (
              <div
                key={conversation.chat_id}
                className={`mb-2 flex w-full items-start gap-2 rounded-md border px-3 py-2 text-left transition ${
                  selectedChatId === conversation.chat_id
                    ? 'border-emerald-400 bg-emerald-500/10'
                    : isDarkMode
                    ? 'border-slate-800 hover:border-slate-700 hover:bg-slate-800/70'
                    : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50'
                }`}
              >
                <button
                  type="button"
                  onClick={() => {
                    setSelectedChatId(conversation.chat_id);
                    setActiveView('debug');
                  }}
                  className="min-w-0 flex-1 text-left"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate font-mono text-xs">{conversation.chat_id.slice(0, 8)}...</span>
                    <span className="shrink-0 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                      {conversation.debug_audio_count} Audio
                    </span>
                  </div>
                  <div className={`mt-1 line-clamp-2 text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                    {conversation.preview || '(kein User-Text)'}
                  </div>
                  <div className={`mt-1 text-[10px] ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {formatDateTime(conversation.last_modified)}
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteDebugConversation(conversation.chat_id)}
                  disabled={Boolean(deletingConversationIds[conversation.chat_id])}
                  className={`mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition disabled:cursor-wait disabled:opacity-50 ${
                    isDarkMode ? 'border-slate-700 text-slate-400 hover:bg-red-500/10 hover:text-red-300' : 'border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600'
                  }`}
                  aria-label="Debug-Gespräch löschen"
                  title="Debug-Gespräch löschen"
                >
                  {deletingConversationIds[conversation.chat_id] ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Trash2 className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        </aside>

        <section className={`min-h-[520px] min-w-0 overflow-hidden rounded-lg border ${isDarkMode ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white'}`}>
          {activeView === 'talk' ? (
            <div className="flex min-h-[520px] flex-col items-center justify-center px-6 text-center">
              <Mic className="mb-4 h-10 w-10 text-emerald-500" />
              <h2 className="text-xl font-semibold">Debug Voice Talk starten</h2>
              <p className={`mt-2 max-w-xl text-sm leading-6 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Der Voice Talk nutzt dieselben Realtime- und Audio-Defaults wie die normale Seite. Der Avatar läuft hier automatisch im Sandbox-Modus.
              </p>
              <button
                type="button"
                onClick={() => setVoiceOpen(true)}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-emerald-400"
              >
                <Mic className="h-4 w-4" />
                Gespräch beginnen
              </button>
            </div>
          ) : (
            <div className="min-h-[520px]">
              <div className={`flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3 ${isDarkMode ? 'border-slate-800' : 'border-slate-200'}`}>
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold">
                    {debugModeView === 'pronunciation' ? 'Aussprache-Debugging' : 'Transkript-Audio-Vergleich'}
                  </h2>
                  <p className={`mt-1 truncate text-xs ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                    {selectedSummary ? selectedSummary.chat_id : 'Kein Gespräch ausgewählt'}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {selectedConversation?.debug_metadata && (
                    <div className={`max-w-full rounded-md px-3 py-2 text-xs ${isDarkMode ? 'bg-slate-950 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                      {debugModeView === 'pronunciation' ? (
                        <>
                          Voice: {selectedConversation.debug_metadata?.backend?.realtime_voice || '-'} ·
                          Sally-Audio: {selectedAssistantAudioCount}
                        </>
                      ) : (
                        <>
                          STT: {selectedConversation.debug_metadata?.transcript_tuning?.transcriptionModel || '-'} ·
                          Noise: {selectedConversation.debug_metadata?.transcript_tuning?.noiseReductionType || '-'} ·
                          Turn: {selectedConversation.debug_metadata?.transcript_tuning?.turnDetectionType || '-'}
                        </>
                      )}
                      {selectedConversationProfile?.name && (
                        <> · Profil: {selectedConversationProfile.name}</>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {detailLoading ? (
                <div className="flex min-h-[440px] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-emerald-500" />
                </div>
              ) : !selectedConversation && debugModeView === 'transcription' ? (
                <div className={`flex min-h-[440px] items-center justify-center px-6 text-center text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-500'}`}>
                  Wählen Sie ein Debug-Gespräch aus oder starten Sie einen neuen Voice Talk.
                </div>
              ) : (
                <div className={
                  debugModeView === 'pronunciation'
                    ? 'grid grid-cols-1 gap-0 xl:grid-cols-[minmax(320px,0.75fr)_minmax(0,1.25fr)]'
                    : 'grid grid-cols-1 gap-0 lg:grid-cols-[minmax(0,1fr)_380px]'
                }>
                  <div className="min-w-0 max-h-[calc(100vh-190px)] overflow-y-auto p-4">
                    {!selectedConversation ? (
                      <div className={`flex min-h-[440px] items-center justify-center rounded-lg border border-dashed px-6 text-center text-sm ${
                        isDarkMode ? 'border-orange-500/30 text-orange-100' : 'border-orange-200 text-orange-900'
                      }`}>
                        Aussprache-Profil und Vocabulary können rechts auch ohne ausgewählten Chat bearbeitet werden.
                      </div>
                    ) : debugModeView === 'transcription' ? (
                      <div className="space-y-3">
                        {selectedConversation.messages.map((message, index) => {
                          const audioItems = getTranscriptionAudio(message);
                          const isUser = message.role === 'user';
                          return (
                            <div
                              key={`${message.role}-${index}`}
                              className={`rounded-lg border px-4 py-3 ${
                                isUser
                                  ? isDarkMode
                                    ? 'border-emerald-500/30 bg-emerald-950/20'
                                    : 'border-emerald-100 bg-emerald-50'
                                  : isDarkMode
                                  ? 'border-slate-800 bg-slate-950'
                                  : 'border-slate-200 bg-slate-50'
                              }`}
                            >
                              <div className={`mb-1 text-xs font-semibold uppercase ${isUser ? 'text-emerald-600' : 'text-slate-500'}`}>
                                {isUser ? 'User' : 'Sally'}
                              </div>
                              <div className="whitespace-pre-wrap text-sm leading-6">{message.content || '(leere Nachricht)'}</div>
                              {audioItems.map((audio) => (
                                <React.Fragment key={audio.audio_id}>
                                  <DebugAudioControl
                                    chatId={selectedConversation.chat_id}
                                    audio={audio}
                                    getDebugAudioUrl={getDebugAudioUrl}
                                  />
                                  {isUser && (
                                    <EvaluationEditor
                                      chatId={selectedConversation.chat_id}
                                      message={message}
                                      messageIndex={index}
                                      audio={audio}
                                      aspect="transcription"
                                      profileId={selectedConversationProfile?.id || null}
                                      evaluation={findEvaluation(index, audio.audio_id, 'transcription')}
                                      draft={evaluationDrafts[evaluationKey(selectedConversation.chat_id, index, audio.audio_id, 'transcription')]}
                                      isDarkMode={isDarkMode}
                                      onDraftChange={handleEvaluationDraftChange}
                                      onSave={handleSaveEvaluation}
                                    />
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {selectedAssistantAudioCount === 0 && (
                          <div className={`rounded-lg border px-4 py-5 text-sm leading-6 ${
                            isDarkMode ? 'border-orange-500/20 bg-orange-950/10 text-orange-100' : 'border-orange-100 bg-orange-50 text-orange-900'
                          }`}>
                            Für ältere Debug-Gespräche wurde Sallys Ausgabe-Audio noch nicht gespeichert.
                            Starten Sie ein neues Debug-Gespräch, um Aussprache-Audio neben dem gesprochenen Text zu vergleichen.
                          </div>
                        )}
                        {selectedConversation.messages.map((message, index) => {
                          if (message.role !== 'assistant') return null;
                          const audioItems = getPronunciationAudio(message);
                          if (!message.content && audioItems.length === 0) return null;
                          return (
                            <div
                              key={`${message.role}-${index}`}
                              className={`rounded-lg border px-4 py-3 ${
                                isDarkMode
                                  ? 'border-orange-500/25 bg-orange-950/10'
                                  : 'border-orange-100 bg-white'
                              }`}
                            >
                              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                                <div className="text-xs font-semibold uppercase text-orange-600">Sally Aussprache</div>
                                <div className={`rounded px-2 py-1 text-[10px] ${isDarkMode ? 'bg-slate-950 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                                  Nachricht {index + 1}
                                </div>
                              </div>
                              <div className="whitespace-pre-wrap text-sm leading-6">{message.content || '(leere Nachricht)'}</div>
                              {audioItems.length > 0 ? (
                                audioItems.map((audio) => (
                                  <React.Fragment key={audio.audio_id}>
                                    <DebugAudioControl
                                      chatId={selectedConversation.chat_id}
                                      audio={audio}
                                      getDebugAudioUrl={getDebugAudioUrl}
                                    />
                                    <EvaluationEditor
                                      chatId={selectedConversation.chat_id}
                                      message={message}
                                      messageIndex={index}
                                      audio={audio}
                                      aspect="pronunciation"
                                      profileId={selectedConversationProfile?.id || null}
                                      evaluation={findEvaluation(index, audio.audio_id, 'pronunciation')}
                                      draft={evaluationDrafts[evaluationKey(selectedConversation.chat_id, index, audio.audio_id, 'pronunciation')]}
                                      isDarkMode={isDarkMode}
                                      onDraftChange={handleEvaluationDraftChange}
                                      onSave={handleSaveEvaluation}
                                    />
                                  </React.Fragment>
                                ))
                              ) : (
                                <div className={`mt-3 rounded-md border px-3 py-2 text-xs ${
                                  isDarkMode ? 'border-slate-800 text-slate-500' : 'border-slate-200 text-slate-500'
                                }`}>
                                  Kein Sally-Audio für diese Nachricht gespeichert.
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  <aside className={`min-w-0 max-h-[calc(100vh-190px)] overflow-y-auto border-t p-4 text-xs ${debugModeView === 'pronunciation' ? 'xl:border-l xl:border-t-0' : 'lg:border-l lg:border-t-0'} ${isDarkMode ? 'border-slate-800 text-slate-400' : 'border-slate-200 text-slate-600'}`}>
                    {debugModeView === 'pronunciation' ? (
                      <div className="space-y-4">
                        <div className={`rounded-md border px-3 py-3 ${isDarkMode ? 'border-orange-500/30 bg-orange-950/10' : 'border-orange-100 bg-orange-50'}`}>
                          <div className="mb-3 flex items-start justify-between gap-2">
                            <div>
                              <div className="font-semibold text-orange-500">Aussprache-Profil</div>
                              <div className="mt-1 text-[11px]">
                                {selectedProfile?.locked ? 'Werkeinstellung ist geschützt. Neues Profil anlegen oder klonen.' : 'Dieses Profil ist bearbeitbar.'}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <button
                                type="button"
                                onClick={handleCreatePronunciationProfile}
                                className="rounded-md bg-orange-500 px-2 py-1.5 font-semibold text-white transition hover:bg-orange-400"
                              >
                                Neu
                              </button>
                              <button
                                type="button"
                                onClick={handleCloneSelectedProfile}
                                className={`rounded-md border px-2 py-1.5 font-semibold transition ${isDarkMode ? 'border-slate-700 hover:bg-slate-800' : 'border-orange-200 hover:bg-white'}`}
                              >
                                Klonen
                              </button>
                            </div>
                          </div>

                          <div className="space-y-2">
                            <input
                              value={profileNameDraft}
                              disabled={selectedProfile?.locked || selectedProfile?.factory}
                              onChange={(event) => setProfileNameDraft(event.target.value)}
                              className={`w-full rounded-md border px-3 py-2 text-xs outline-none disabled:opacity-60 ${
                                isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-orange-200 bg-white text-slate-900'
                              }`}
                              placeholder="Profilname"
                            />
                            <textarea
                              value={profileDescriptionDraft}
                              disabled={selectedProfile?.locked || selectedProfile?.factory}
                              onChange={(event) => setProfileDescriptionDraft(event.target.value)}
                              rows={2}
                              className={`w-full resize-y rounded-md border px-3 py-2 text-xs outline-none disabled:opacity-60 ${
                                isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-600' : 'border-orange-200 bg-white text-slate-900 placeholder:text-slate-400'
                              }`}
                              placeholder="Beschreibung"
                            />
                            <div className="grid grid-cols-2 gap-2">
                              <select
                                value={profileTargetDraft}
                                disabled={selectedProfile?.locked || selectedProfile?.factory}
                                onChange={(event) => setProfileTargetDraft(event.target.value)}
                                className={`rounded-md border px-2 py-2 text-xs outline-none disabled:opacity-60 ${
                                  isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-orange-200 bg-white text-slate-900'
                                }`}
                              >
                                <option value="balanced">Balanced</option>
                                <option value="transcription">Transkript</option>
                                <option value="pronunciation">Aussprache</option>
                              </select>
                              <select
                                value={profileStatusDraft}
                                disabled={selectedProfile?.locked || selectedProfile?.factory}
                                onChange={(event) => setProfileStatusDraft(event.target.value)}
                                className={`rounded-md border px-2 py-2 text-xs outline-none disabled:opacity-60 ${
                                  isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-orange-200 bg-white text-slate-900'
                                }`}
                              >
                                <option value="draft">Draft</option>
                                <option value="candidate">Candidate</option>
                                <option value="approved">Approved</option>
                              </select>
                            </div>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-orange-500">Realtime Voice</span>
                              <select
                                value={profileVoiceDraft}
                                disabled={selectedProfile?.locked || selectedProfile?.factory}
                                onChange={(event) => setProfileVoiceDraft(event.target.value)}
                                className={`w-full rounded-md border px-3 py-2 text-xs outline-none disabled:opacity-60 ${
                                  isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100' : 'border-orange-200 bg-white text-slate-900'
                                }`}
                              >
                                {REALTIME_VOICE_OPTIONS.map((voice) => (
                                  <option key={voice} value={voice}>{voice}</option>
                                ))}
                              </select>
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-orange-500">Debug Replacement Overrides</span>
                              <textarea
                                value={replacementDraft}
                                disabled={selectedProfile?.locked || selectedProfile?.factory}
                                onChange={(event) => setReplacementDraft(event.target.value)}
                                rows={3}
                                className={`w-full resize-y rounded-md border px-3 py-2 font-mono text-[11px] outline-none disabled:opacity-60 ${
                                  isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-600' : 'border-orange-200 bg-white text-slate-900 placeholder:text-slate-400'
                                }`}
                                placeholder={'SAP=Ess A Pe\nS/4HANA=Ess vier Hana'}
                              />
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-orange-500">Debug Aussprache-Notizen</span>
                              <textarea
                                value={pronunciationNotesDraft}
                                disabled={selectedProfile?.locked || selectedProfile?.factory}
                                onChange={(event) => setPronunciationNotesDraft(event.target.value)}
                                rows={2}
                                className={`w-full resize-y rounded-md border px-3 py-2 text-xs outline-none disabled:opacity-60 ${
                                  isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-600' : 'border-orange-200 bg-white text-slate-900 placeholder:text-slate-400'
                                }`}
                                placeholder="Aussprache-Notizen nur für diese Debug-Session"
                              />
                            </label>
                            <label className="block">
                              <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-orange-500">Phrase Tests</span>
                              <textarea
                                value={phraseTestsDraft}
                                disabled={selectedProfile?.locked || selectedProfile?.factory}
                                onChange={(event) => setPhraseTestsDraft(event.target.value)}
                                rows={3}
                                className={`w-full resize-y rounded-md border px-3 py-2 font-mono text-[11px] outline-none disabled:opacity-60 ${
                                  isDarkMode ? 'border-slate-700 bg-slate-950 text-slate-100 placeholder:text-slate-600' : 'border-orange-200 bg-white text-slate-900 placeholder:text-slate-400'
                                }`}
                                placeholder={'Willkommen bei Synaworks. => Erwartet: aktuelle Aussprache\nSynaworks unterstützt SAP-IT.'}
                              />
                            </label>
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={handleSaveProfileDraft}
                                disabled={profileLoading || selectedProfile?.locked || selectedProfile?.factory}
                                className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-400 disabled:cursor-not-allowed disabled:bg-slate-600 disabled:text-slate-300"
                              >
                                <Save className="h-3.5 w-3.5" />
                                Profil speichern
                              </button>
                              <button
                                type="button"
                                onClick={handleDeleteSelectedProfile}
                                disabled={!selectedProfile || selectedProfile.locked || selectedProfile.factory}
                                className={`inline-flex h-8 w-8 items-center justify-center rounded-md border transition disabled:cursor-not-allowed disabled:opacity-40 ${
                                  isDarkMode ? 'border-slate-700 text-slate-400 hover:bg-slate-800' : 'border-orange-200 text-slate-500 hover:bg-white'
                                }`}
                                aria-label="Profil löschen"
                                title="Profil löschen"
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                            {profileMessage && (
                              <div className={`rounded-md px-3 py-2 text-xs ${isDarkMode ? 'bg-slate-950 text-slate-300' : 'bg-white text-slate-700'}`}>
                                {profileMessage}
                              </div>
                            )}
                            <div className={`rounded-md px-3 py-2 text-[11px] ${isDarkMode ? 'bg-slate-950 text-slate-400' : 'bg-white text-slate-600'}`}>
                              <div className="font-semibold">Abweichungen zur Werkeinstellung</div>
                              <div className="mt-1">{profileDiffs.length > 0 ? profileDiffs.join(', ') : 'Keine'}</div>
                            </div>
                          </div>
                        </div>

                        <div className={`rounded-md border px-3 py-3 ${isDarkMode ? 'border-slate-800 bg-slate-950' : 'border-slate-200 bg-white'}`}>
                          <div className="mb-3 flex items-center justify-between gap-2">
                            <div>
                              <div className="font-semibold text-slate-500">Globales Vocabulary</div>
                              <div className="mt-1 text-[11px]">Wirkt auch in Production. Tests besser über Debug-Override.</div>
                            </div>
                            <button
                              type="button"
                              onClick={handleCreateVocabularyWord}
                              className={`rounded-md border px-2 py-1.5 font-semibold transition ${isDarkMode ? 'border-slate-700 hover:bg-slate-900' : 'border-slate-200 hover:bg-slate-50'}`}
                            >
                              Neues Wort
                            </button>
                          </div>
                          <div className="space-y-2">
                            <select
                              value={selectedVocabularyWord?.word || ''}
                              onChange={(event) => setSelectedVocabularyWordKey(vocabWordKey(event.target.value))}
                              className={`w-full rounded-md border px-3 py-2 text-xs outline-none ${
                                isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
                              }`}
                            >
                              <option value="">Neues Vocabulary-Wort</option>
                              {vocabWords.map((word) => (
                                <option key={word.word} value={word.word}>{word.word}</option>
                              ))}
                            </select>
                            <div className={`rounded-md border px-2 py-2 text-[11px] ${
                              isDarkMode ? 'border-amber-500/40 bg-amber-500/10 text-amber-100' : 'border-amber-200 bg-amber-50 text-amber-900'
                            }`}>
                              Effektiv für "{activeVocabularyWordName}": {effectiveVocabularyReplacement || 'kein Replacement'} ({effectiveVocabularySource})
                            </div>
                            <input
                              value={vocabularyDraft.word}
                              onChange={(event) => setVocabularyDraft((prev) => ({ ...prev, word: event.target.value }))}
                              className={`w-full rounded-md border px-2 py-1.5 font-mono text-[11px] outline-none ${
                                isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
                              }`}
                              placeholder="Wort, z. B. Synaworks"
                            />
                            <input
                              value={vocabularyDraft.context}
                              onChange={(event) => setVocabularyDraft((prev) => ({ ...prev, context: event.target.value }))}
                              className={`w-full rounded-md border px-2 py-1.5 text-[11px] outline-none ${
                                isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
                              }`}
                              placeholder="Kontext"
                            />
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <input
                                value={vocabularyDraft.pronunciation}
                                onChange={(event) => setVocabularyDraft((prev) => ({ ...prev, pronunciation: event.target.value }))}
                                className={`rounded-md border px-2 py-1.5 font-mono text-[11px] outline-none ${
                                  isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
                                }`}
                                placeholder="Reference Pronunciation"
                              />
                              <input
                                value={vocabularyDraft.output_pronunciation || ''}
                                onChange={(event) => setVocabularyDraft((prev) => ({ ...prev, output_pronunciation: event.target.value }))}
                                className={`rounded-md border px-2 py-1.5 font-mono text-[11px] outline-none ${
                                  isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
                                }`}
                                placeholder="Output Pronunciation"
                              />
                            </div>
                            <input
                              value={vocabularyDraft.voice_replacement || ''}
                              onChange={(event) => setVocabularyDraft((prev) => ({ ...prev, voice_replacement: event.target.value }))}
                              className={`w-full rounded-md border px-2 py-1.5 font-mono text-[11px] outline-none ${
                                isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100' : 'border-slate-200 bg-white text-slate-900'
                              }`}
                              placeholder="Voice Replacement"
                            />
                            <textarea
                              value={vocabularyAliasesDraft}
                              onChange={(event) => setVocabularyAliasesDraft(event.target.value)}
                              rows={4}
                              className={`w-full resize-y rounded-md border px-2 py-1.5 font-mono text-[11px] outline-none ${
                                isDarkMode ? 'border-slate-700 bg-slate-900 text-slate-100 placeholder:text-slate-600' : 'border-slate-200 bg-white text-slate-900 placeholder:text-slate-400'
                              }`}
                              placeholder={'Input Aliases, eine Zeile pro Alias'}
                            />
                            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                              <button
                                type="button"
                                onClick={handleSetVocabularyDebugOverride}
                                disabled={profileLoading || selectedProfile?.locked || selectedProfile?.factory}
                                className={`rounded-md border px-2 py-2 font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${
                                  isDarkMode ? 'border-slate-700 text-slate-200 hover:bg-slate-900' : 'border-slate-200 text-slate-700 hover:bg-slate-50'
                                }`}
                              >
                                Debug-Override speichern
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveVocabularyDraft}
                                disabled={vocabLoading}
                                className="rounded-md bg-amber-600 px-2 py-2 font-semibold text-white transition hover:bg-amber-500 disabled:cursor-wait disabled:opacity-60"
                              >
                                Production-Vocabulary speichern
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className={`rounded-md border px-3 py-3 ${isDarkMode ? 'border-orange-500/30 bg-orange-950/10 text-orange-100' : 'border-orange-100 bg-orange-50 text-orange-950'}`}>
                          <div className="mb-2 flex items-center justify-between gap-2">
                            <div className="font-semibold">Pronunciation Lab</div>
                            <span className="rounded bg-black/10 px-1.5 py-0.5 font-mono text-[10px]">{profileVoiceDraft}</span>
                          </div>
                          <textarea
                            value={pronunciationTestPhraseDraft}
                            onChange={(event) => {
                              pronunciationTestPhraseTouchedRef.current = true;
                              setPronunciationTestPhraseDraft(event.target.value);
                            }}
                            rows={4}
                            className={`w-full resize-y rounded-md border px-2 py-2 text-xs outline-none ${
                              isDarkMode ? 'border-orange-500/30 bg-slate-950 text-slate-100' : 'border-orange-200 bg-white text-slate-900'
                            }`}
                            placeholder={DEFAULT_PRONUNCIATION_TEST_PHRASE}
                          />
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => handleRunPronunciationTests(1)}
                              disabled={pronunciationLabRunning || !selectedProfile}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-orange-500 px-3 py-2 text-xs font-semibold text-white transition hover:bg-orange-400 disabled:cursor-wait disabled:opacity-60"
                            >
                              <Play className="h-3.5 w-3.5" />
                              1x testen
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRunPronunciationTests(5)}
                              disabled={pronunciationLabRunning || !selectedProfile}
                              className="inline-flex flex-1 items-center justify-center gap-2 rounded-md bg-slate-800 px-3 py-2 text-xs font-semibold text-white transition hover:bg-slate-700 disabled:cursor-wait disabled:opacity-60"
                            >
                              <Volume2 className="h-3.5 w-3.5" />
                              5x testen
                            </button>
                          </div>
                          {pronunciationLabMessage && (
                            <div className="mt-2 rounded bg-black/10 px-2 py-1 text-[11px]">{pronunciationLabMessage}</div>
                          )}
                          {pronunciationRuns.length > 0 && (
                            <div className={`mt-3 grid grid-cols-2 gap-2 text-[10px] sm:grid-cols-4 ${
                              isDarkMode ? 'text-slate-300' : 'text-slate-600'
                            }`}>
                              <div className={`rounded border px-2 py-1 ${isDarkMode ? 'border-slate-800 bg-slate-950' : 'border-orange-100 bg-white'}`}>
                                <div className="font-semibold">Erfolgreich</div>
                                <div className="font-mono">{pronunciationLabStats.done}/{pronunciationLabStats.total}</div>
                              </div>
                              <div className={`rounded border px-2 py-1 ${isDarkMode ? 'border-slate-800 bg-slate-950' : 'border-orange-100 bg-white'}`}>
                                <div className="font-semibold">Replacement</div>
                                <div className="font-mono">{pronunciationLabStats.replaced}/{pronunciationLabStats.total}</div>
                              </div>
                              <div className={`rounded border px-2 py-1 ${isDarkMode ? 'border-slate-800 bg-slate-950' : 'border-orange-100 bg-white'}`}>
                                <div className="font-semibold">Bewertung</div>
                                <div className="font-mono">{pronunciationLabStats.pass} gut / {pronunciationLabStats.fail} falsch</div>
                              </div>
                              <div className={`rounded border px-2 py-1 ${isDarkMode ? 'border-slate-800 bg-slate-950' : 'border-orange-100 bg-white'}`}>
                                <div className="font-semibold">Fehler</div>
                                <div className="font-mono">{pronunciationLabStats.error}</div>
                              </div>
                            </div>
                          )}
                          <div className="mt-3 space-y-2">
                            {pronunciationRuns.length === 0 ? (
                              <div className="rounded border border-dashed border-orange-300/50 px-2 py-3 text-center text-[11px] opacity-80">
                                Noch keine Testläufe.
                              </div>
                            ) : (
                              pronunciationRuns.slice(0, 8).map((run) => (
                                <div key={run.id} className={`rounded-md border px-2 py-2 ${isDarkMode ? 'border-slate-800 bg-slate-950' : 'border-orange-100 bg-white'}`}>
                                  <div className="mb-1 flex items-center justify-between gap-2">
                                    <span className="truncate font-mono text-[10px]">{run.chatId}</span>
                                    <div className="flex shrink-0 items-center gap-1">
                                      <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${
                                        run.status === 'done'
                                          ? 'bg-emerald-500/20 text-emerald-500'
                                          : run.status === 'error'
                                          ? 'bg-red-500/20 text-red-500'
                                          : 'bg-amber-500/20 text-amber-500'
                                      }`}>
                                        {run.status}
                                      </span>
                                      <button
                                        type="button"
                                        onClick={() => handleDeletePronunciationRun(run)}
                                        disabled={run.status === 'running' || Boolean(deletingRunIds[run.id])}
                                        className={`inline-flex h-6 w-6 items-center justify-center rounded border transition disabled:cursor-wait disabled:opacity-50 ${
                                          isDarkMode ? 'border-slate-700 text-slate-400 hover:bg-red-500/10 hover:text-red-300' : 'border-orange-100 text-slate-500 hover:bg-red-50 hover:text-red-600'
                                        }`}
                                        aria-label="Testlauf löschen"
                                        title="Testlauf löschen"
                                      >
                                        {deletingRunIds[run.id] ? (
                                          <Loader2 className="h-3 w-3 animate-spin" />
                                        ) : (
                                          <Trash2 className="h-3 w-3" />
                                        )}
                                      </button>
                                    </div>
                                  </div>
                                  <div className="break-words text-[11px]"><span className="font-semibold">Prompt:</span> {run.phrase}</div>
                                  {run.spokenPhrase && (
                                    <div className="mt-1 break-words font-mono text-[10px] opacity-80">OpenAI bekommt: {run.spokenPhrase}</div>
                                  )}
                                  <div className={`mt-2 grid grid-cols-2 gap-1 text-[10px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                                    <div>
                                      <span className="font-semibold">Replacement:</span>{' '}
                                      {run.replacementApplied ? 'angewendet' : run.spokenPhrase ? 'unverändert' : '-'}
                                    </div>
                                    <div><span className="font-semibold">Voice:</span> {run.voice || '-'}</div>
                                    <div><span className="font-semibold">Laufzeit:</span> {formatShortDuration(run.durationMs)}</div>
                                    <div>
                                      <span className="font-semibold">Audio:</span>{' '}
                                      {run.audioChunkCount ?? '-'} Chunks / {formatShortDuration(run.audioDurationMs)}
                                    </div>
                                  </div>
                                  {run.transcript && (
                                    <div className="mt-1 break-words text-[11px] opacity-80">Transcript: {run.transcript}</div>
                                  )}
                                  {run.audioUrl && <audio className="mt-2 w-full" controls src={run.audioUrl} />}
                                  <div className="mt-2 flex flex-wrap gap-1">
                                    <button
                                      type="button"
                                      onClick={() => setPronunciationRunVerdict(run.id, 'pass')}
                                      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-semibold transition ${
                                        run.verdict === 'pass'
                                          ? 'border-emerald-500 bg-emerald-500/15 text-emerald-500'
                                          : isDarkMode
                                          ? 'border-slate-800 text-slate-400 hover:bg-slate-900'
                                          : 'border-orange-100 text-slate-500 hover:bg-orange-50'
                                      }`}
                                    >
                                      <CheckCircle2 className="h-3 w-3" />
                                      Gut
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setPronunciationRunVerdict(run.id, 'fail')}
                                      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-semibold transition ${
                                        run.verdict === 'fail'
                                          ? 'border-red-500 bg-red-500/15 text-red-500'
                                          : isDarkMode
                                          ? 'border-slate-800 text-slate-400 hover:bg-slate-900'
                                          : 'border-orange-100 text-slate-500 hover:bg-orange-50'
                                      }`}
                                    >
                                      <XCircle className="h-3 w-3" />
                                      Falsch
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setPronunciationRunVerdict(run.id, 'unclear')}
                                      className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-semibold transition ${
                                        run.verdict === 'unclear'
                                          ? 'border-amber-500 bg-amber-500/15 text-amber-500'
                                          : isDarkMode
                                          ? 'border-slate-800 text-slate-400 hover:bg-slate-900'
                                          : 'border-orange-100 text-slate-500 hover:bg-orange-50'
                                      }`}
                                    >
                                      <HelpCircle className="h-3 w-3" />
                                      Unklar
                                    </button>
                                  </div>
                                  {run.error && <div className="mt-1 text-red-500">{run.error}</div>}
                                </div>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="mb-4">
                          <div className="font-semibold text-slate-500">Zeitpunkt</div>
                          <div>{formatDateTime(selectedConversation?.created_at)}</div>
                        </div>
                        <div className="mb-4">
                          <div className="font-semibold text-slate-500">User-Agent</div>
                          <div className="mt-1 break-words font-mono text-[11px]">{selectedConversation?.user_agent || '-'}</div>
                        </div>
                        <div className="mb-4">
                          <div className="font-semibold text-slate-500">Profil-Snapshot</div>
                          <div className="mt-1">
                            {selectedConversationProfile?.name || selectedConversationProfile?.id || 'Kein Profil-Snapshot'}
                          </div>
                        </div>
                        <div className="mb-4">
                          <div className="font-semibold text-slate-500">Bewertungen</div>
                          <div className="mt-1">{evaluations.length} Einträge</div>
                        </div>
                        <div>
                          <div className="font-semibold text-slate-500">Debug-Metadaten</div>
                          <pre className={`mt-2 max-h-72 overflow-auto rounded-md p-3 text-[10px] ${isDarkMode ? 'bg-slate-950 text-slate-400' : 'bg-slate-100 text-slate-600'}`}>
                            {JSON.stringify(selectedConversation?.debug_metadata || {}, null, 2)}
                          </pre>
                        </div>
                      </>
                    )}
                  </aside>
                </div>
              )}
            </div>
          )}
        </section>
      </div>

      <VoiceChatModal
        key={selectedProfile?.id || 'debug-profile'}
        isOpen={voiceOpen}
        onClose={handleVoiceClose}
        isDarkMode={isDarkMode}
        companyName="Synaworks"
        debugMode
        debugProfile={selectedProfile}
        onSaveDebugProfileSettings={handleSaveRuntimeProfileSettings}
      />
    </main>
  );
};

export default DebugPage;
